import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CarChangeProductBodyRequest } from '@models/car-change-product';
import { Observable } from 'rxjs';
import { MMobileService } from '../../../core/services/common';
import {
  MailMessage,
  MailMessageRemoteConfig,
  MailParticularData,
  SendMessageRequest
} from '../../models/mail';
import { Document, Policy, PolicyReceipt, PolicySinister } from '../../models/policy';
import { PolicyCar, SuperbonusOfferType } from '../../models/policy-car';
import { PendingReceipt } from '../../models/receipt';
import { SinisterConsultationDetail } from '../../models/sinister-auto-consultation';
import { PoliciesContextService } from '../context/policies-context.service';
import { AuthMirrorService } from '../core/auth/auth-mirror.service';
import { BaseService } from '../core/base.service';
import { OperationRegisterService } from '../core/operation-register.service';
import { BaseMailService } from './base-mail.service';

@Injectable({
  providedIn: 'root'
})
export class CarPoliciesService {
  constructor(
    private http: HttpClient,
    private base: BaseService,
    private baseMailService: BaseMailService,
    private mmobile: MMobileService,
    private authMirror: AuthMirrorService,
    private or: OperationRegisterService,
    private policiesCtx: PoliciesContextService
  ) { }

  getCarPolicy(policy: PolicyCar): Observable<PolicyCar> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies/${policy?.policyNumber}`;
    const params = this.or.setAutoParams(policy);
    return this.http.get<PolicyCar>(
      url,
      {
        ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
        params
      }
    );
  }

  getCarReceipts(policy: PolicyCar): Observable<PolicyReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies/${policy.policyNumber
      }/receipts?companyId=${policy.companyId}`;
    const params = this.or.setAutoParams(policy);
    return this.http.get<PolicyReceipt[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getCarReceipt(
    policy: PolicyCar,
    carReceiptNumber: string
  ): Observable<PolicyReceipt> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies/${policy.policyNumber
      }/receipts/${carReceiptNumber}?companyId=${policy.companyId}`;
    const params = this.or.setAutoParams(policy);
    return this.http.get<PolicyReceipt>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getCarPendingReceipts(
    carPolicyNumber: string,
    carCompanyId: string,
    params: any
  ): Observable<PendingReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies/${carPolicyNumber}/receiptsPayment/receipts?companyId=${carCompanyId}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<PendingReceipt[]>(url, { ...headers, params });
  }

  getCarSinisters(policy: PolicyCar, clearCache = false): Observable<PolicySinister[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies/${policy.policyNumber
      }/sinisters`;
    const params = this.or.setAutoParams(policy);
    return this.http.get<PolicySinister[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true, clearCache }),
      params
    });
  }

  getCarSinister(
    policy: PolicyCar,
    carSinisterCode: string,
    hasServiceCenter = false
  ) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies/${policy.policyNumber
      }/sinisters/${carSinisterCode}`;
    let params = this.or.setAutoParams(policy);
    params = params.set(
      'hasServiceCenter',
      hasServiceCenter.toString() || 'false'
    );
    return this.http.get<SinisterConsultationDetail>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getCarReceiptDocument(
    carPolicyNumber: string,
    carCompanyId: string,
    carReceiptNumber: string,
    params: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies/${carPolicyNumber}/documents/receipts/${carReceiptNumber}?companyId=${carCompanyId}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getCarParticularConditions(
    carPolicyNumber: string,
    params?: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies/${carPolicyNumber}/documents/particularConditions`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getCarGeneralConditions(
    carPolicyNumber: string,
    generalConditionsCode: string,
    params: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies/${carPolicyNumber}/documents/generalConditions?generalConditionsCode=${generalConditionsCode}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, {
      ...headers,
      params
    });
  }

  getCarRenewalNotice(policy: Policy): Observable<Document> {
    const { policyNumber: carPolicyNumber } = policy || {};
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies/${carPolicyNumber}/documents/renewalNotice`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.calculateDocumentROParams(policy);
    return this.http.get<Document>(
      url,
      { ...headers, params }
    );
  }

  exchangeSuperbonus(
    carPolicyNumber: string,
    payMethod: SuperbonusOfferType,
    assistancePhone: string,
    headerParams?: any
  ) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies/${carPolicyNumber}/superbonus/exchange`;
    const requestBody = JSON.stringify({
      paymentMethod: payMethod,
      assistancePhoneNumber: assistancePhone
    });
    const headers = this.base.getPrivateOIMHeaders({
      cacheResponse: false,
      clearCache: true
    });
    this.policiesCtx.clear();
    return this.http.post(url, requestBody, {
      ...headers,
      params: headerParams
    });
  }

  sendMessageMoreInfoSinister(
    name: string,
    plate: string,
    policy: string,
    phone: string,
    email: string,
    nif: string,
    query: string
  ) {
    const forms: MailMessageRemoteConfig[] = this.mmobile.getCustomConfig().forms || [];
    const { user } = this.authMirror.authService.getState();
    const userIsCompany = this.authMirror.authService.checkUserIsCompany();
    const userEmail = userIsCompany ? user.legalRepresentative.email : user.email;
    const msg: MailMessage = {
      sendingFile: false,
      read: true,
      mailBoxId: '2',
      inboxId: '1',
      responseModeId: '1',
      type: '0',
      frmId: '287',
      groupId: '264',
      from: userEmail ? userEmail : null
    };

    const mailBoxForm = forms?.find(f => f.form === 'INFORMACIÓN_SINIESTROS_AUTOS');

    if (mailBoxForm) {
      msg.mailBoxId = mailBoxForm.mailBoxId;
      msg.inboxId = mailBoxForm.inboxId;
      msg.responseModeId = mailBoxForm.responseModeId;
      msg.type = mailBoxForm.type;
      msg.frmId = mailBoxForm.frmId;
      msg.groupId = mailBoxForm.groupId;
    }
    const data: MailParticularData[] = [];
    data.push({ field: 'nombre', value: name });
    data.push({ field: 'matricula', value: plate });
    data.push({ field: 'poliza', value: policy });
    data.push({ field: 'telefono', value: phone });
    data.push({ field: 'email', value: email });
    data.push({ field: 'nif', value: nif });
    data.push({ field: 'consulta', value: query });
    const request: SendMessageRequest = {
      mailboxIdentifier: user.documentNumber,
      particularData: data,
      message: msg
    };
    return this.baseMailService.sendMessage(request);
  }

  changeUsualDriverAddress(policy: PolicyCar, body: any): Observable<any> {
    const url = this.base.getPolicyTypeBaseUrl(policy, 'addressChange');
    const params = this.or.getUrlQueryParams(policy);

    return this.http.post(url, body, { ...this.base.getPrivateOIMHeaders(), params });
  }

  changeCarProduct(policy, newCarProduct: Partial<CarChangeProductBodyRequest>) {
    let url = this.base.getPolicyTypeBaseUrl(policy, 'productChange');
    url += this.or.getROQueryParams(policy);
    return this.http.post(url, newCarProduct, this.base.getPrivateOIMHeaders());
  }

  getYcarEcoActivitySSOUrl(policyNumber: string, params?: HttpParams) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies/${policyNumber}/status/sso`;
    const headers = this.base.getPrivateOIMHeaders();
    return this.http.get<{ url: string }>(url, { ...headers, params });
  }
}
